import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";
import CheckIcon from "@mui/icons-material/Check";
import CloseIcon from "@mui/icons-material/Close";
import RefreshIcon from "@mui/icons-material/Refresh";
import {
  Box,
  Button,
  FormControl,
  FormHelperText,
  IconButton,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { useState } from "react";
import FlexBox from "../../../../components/layouts/flexbox";
import { showNotification } from "../../../../services/utils";
import {
  aiBlockSx,
  aiBodySx,
  aiIconButtonSx,
  aiIconSx,
  aiLabelSx,
  aiPillSx,
  COLORS,
  formControlSx,
  getBorderWrapperSx,
  getRefreshIconLoadingSx,
  getWriteWithAiButtonSx,
  innerStackSx,
} from "./styles";

export interface TextareaWithAiSuggestionProps {
  label: string;
  required?: boolean;
  value: string;
  onChange: (value: string) => void;
  fetchSuggestion?: (
    payload: Record<string, unknown>
  ) => Promise<{ data?: { description?: string; suggestion?: string } }>;
  getSuggestPayload?: () => Record<string, unknown>;
  suggestionLoading?: boolean;
  error?: string;
  helperText?: string;
  placeholder?: string;
  rows?: number;
  rowsWithSuggestion?: number;
  showAiButton?: boolean;
  minLengthForAi?: number;
}

const DEFAULT_PLACEHOLDER =
  "We have a project in plan for the fiscal year, with budget allocated, to evaluate solutions that will assess the security posture of SaaS applications.";

function TextareaWithAiSuggestion({
  label,
  required = false,
  value,
  onChange,
  fetchSuggestion,
  getSuggestPayload,
  suggestionLoading: suggestionLoadingProp,
  error,
  helperText,
  placeholder = DEFAULT_PLACEHOLDER,
  rows = 4,
  rowsWithSuggestion = 2,
  showAiButton = true,
  minLengthForAi,
}: TextareaWithAiSuggestionProps) {
  const [internalSuggestion, setInternalSuggestion] = useState<string | null>(
    null
  );
  const [hasFetchedOnce, setHasFetchedOnce] = useState(false);
  const [localLoading, setLocalLoading] = useState(false);

  const useInternalApi = Boolean(fetchSuggestion);
  const suggestion = useInternalApi ? internalSuggestion : null;
  const suggestionLoading = useInternalApi
    ? localLoading
    : suggestionLoadingProp ?? false;

  const isSummaryEmpty = !(value?.trim()?.length > 0);
  const showRefreshLabel =
    !isSummaryEmpty && (hasFetchedOnce || suggestion !== null);

  const handleRefreshClick = async () => {
    if (!useInternalApi || !fetchSuggestion) return;

    if (
      minLengthForAi != null &&
      (value ?? "").trim().length < minLengthForAi
    ) {
      showNotification({
        message: `Please enter at least ${minLengthForAi} characters to use Write with AI.`,
        type: "error",
      });
      return;
    }

    setInternalSuggestion(null);
    setLocalLoading(true);
    try {
      const res = await fetchSuggestion(getSuggestPayload?.() ?? {}).catch(
        () => null
      );
      const nextSuggestion =
        res?.data?.description ?? res?.data?.suggestion ?? null;
      if (typeof nextSuggestion === "string") {
        setInternalSuggestion(nextSuggestion);
      }
    } finally {
      setLocalLoading(false);
      setHasFetchedOnce(true);
    }
  };

  const handleAcceptSuggestion = () => {
    if (suggestion) {
      onChange(suggestion);
    }
    setInternalSuggestion(null);
  };

  const handleRejectSuggestion = () => {
    setInternalSuggestion(null);
  };

  const hasSuggestion = suggestion !== null;

  return (
    <Stack gap={1}>
      <FlexBox
        alignItems="center"
        justifyContent={showAiButton ? "space-between" : "flex-start"}
      >
        <Typography fontSize={14} fontWeight={600} color="#3F3D42" component="span">
          {label}
          {required && (
            <Typography component="span" color="error.main">
              *
            </Typography>
          )}
        </Typography>

        <Button
          size="small"
          onClick={handleRefreshClick}
          disabled={suggestionLoading}
          startIcon={
            showRefreshLabel ? (
              <RefreshIcon sx={getRefreshIconLoadingSx(suggestionLoading)} />
            ) : (
              <AutoAwesomeIcon sx={{ color: COLORS.white }} />
            )
          }
          sx={getWriteWithAiButtonSx(suggestionLoading)}
          style={{
            ...(!showAiButton && {
              visibility: "hidden",
            }),
          }}
        >
          {showRefreshLabel ? "Refresh" : "Write with AI"}
        </Button>
      </FlexBox>

      <FormControl variant="outlined" fullWidth error={!!error} sx={formControlSx}>
        <Box sx={getBorderWrapperSx(!!error, suggestionLoading)}>
          <Stack
            sx={{
              ...innerStackSx,
              ...(suggestionLoading && {
                position: "relative",
                zIndex: 1,
                m: "2px",
              }),
            }}
          >
            <TextField
              value={value}
              onChange={(e) => onChange(e.target.value)}
              size="small"
              fullWidth
              multiline
              rows={hasSuggestion ? rowsWithSuggestion : rows}
              placeholder={placeholder}
            />
            {hasSuggestion && (
              <Stack gap={1.25} sx={aiBlockSx}>
                <FlexBox sx={aiPillSx}>
                  <AutoAwesomeIcon sx={aiIconSx} />
                  <Typography sx={aiLabelSx}>AI Generate</Typography>
                </FlexBox>
                <Typography sx={aiBodySx}>{suggestion}</Typography>
                <FlexBox alignItems="flex-end" gap={1}>
                  <IconButton
                    onClick={handleAcceptSuggestion}
                    aria-label="Accept suggestion"
                    title="Accept"
                    sx={aiIconButtonSx}
                  >
                    <CheckIcon sx={aiIconSx} />
                  </IconButton>
                  <IconButton
                    onClick={handleRejectSuggestion}
                    aria-label="Reject suggestion"
                    title="Reject"
                    sx={aiIconButtonSx}
                  >
                    <CloseIcon sx={aiIconSx} />
                  </IconButton>
                </FlexBox>
              </Stack>
            )}
          </Stack>
        </Box>
        {(error || helperText) && (
          <FormHelperText error={!!error} sx={{ ml: 0 }}>
            {error || helperText}
          </FormHelperText>
        )}
      </FormControl>
    </Stack>
  );
}

export { TextareaWithAiSuggestion };
